import React from 'react';

const headerStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'center',
    backgroundColor: '#f9fafb',
    fontWeight: 600,
    fontSize: '13px',
    color: '#374151',
};

const cellStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'center',
    fontSize: '13px',
    color: '#1f2937',
};

const labelCellStyle = {
    ...cellStyle,
    textAlign: 'left',
    fontWeight: 500,
};

const tableStyle = {
    border: '1px solid #e5e7eb',
    borderCollapse: 'collapse',
    width: '100%',
    tableLayout: 'fixed',
};

// Shared column widths so every summary table in the SLA Summary Report lines
// up vertically regardless of its content. Transfer SLA has no per-pax minutes
// target so the Minutes column renders an em-dash for these rows.
const SUMMARY_COLGROUP = (
    <colgroup>
        <col style={{ width: '40%' }} />
        <col style={{ width: '10%' }} />
        <col style={{ width: '10%' }} />
        <col style={{ width: '14%' }} />
        <col style={{ width: '12%' }} />
        <col style={{ width: '14%' }} />
    </colgroup>
);

const parseTarget = (target) => {
    if (!target) return 0;
    return parseFloat(String(target).replace('%', ''));
};

/** Daily KPI cell helper — re-derives counts from rawData (ignoring isIgnored rows) so the table stays consistent if the server hasn't recomputed them. */
const recomputeKpi = (kpi) => {
    if (!kpi) return kpi;
    if (!Array.isArray(kpi.rawData)) {
        return {
            ...kpi,
            totalPass: kpi.totalPass ?? 0,
            total: kpi.total ?? 0,
            score: kpi.score ?? 0,
        };
    }
    const scored = kpi.rawData.filter((r) => !r.isIgnored);
    const totalPass = scored.filter((r) => r.pass === true).length;
    const total = scored.length;
    const score = total > 0 ? Math.round((totalPass / total) * 100) : 0;
    return { ...kpi, totalPass, total, score };
};

const KpiRow = ({ kpi }) => {
    const targetNum = parseTarget(kpi.target);
    const meetsTarget = kpi.score >= targetNum;
    return (
        <tr>
            <td style={labelCellStyle}>{kpi.title}</td>
            <td style={cellStyle}>–</td>
            <td style={cellStyle}>{kpi.target || '–'}</td>
            <td style={cellStyle}>{kpi.totalPass}</td>
            <td style={cellStyle}>{kpi.total}</td>
            <td style={{
                ...cellStyle,
                fontWeight: 600,
                backgroundColor: kpi.total === 0 ? '#f9fafb' : meetsTarget ? '#dcfce7' : '#fee2e2',
                color: kpi.total === 0 ? '#6b7280' : meetsTarget ? '#166534' : '#991b1b',
            }}>
                {kpi.total === 0 ? '-' : `${kpi.score}%`}
            </td>
        </tr>
    );
};

const KpiTable = ({ title, kpis }) => {
    const keys = kpis ? Object.keys(kpis) : [];
    if (!keys.length) return null;
    return (
        <div style={{ marginBottom: '24px' }}>
            <table style={tableStyle}>
                {SUMMARY_COLGROUP}
                <thead>
                    <tr>
                        <th style={headerStyle}>{title}</th>
                        <th style={headerStyle}>Minutes</th>
                        <th style={headerStyle}>Target</th>
                        <th style={headerStyle}>Total Pass</th>
                        <th style={headerStyle}>Total</th>
                        <th style={headerStyle}>Score</th>
                    </tr>
                </thead>
                <tbody>
                    {keys.map((key) => (
                        <KpiRow key={key} kpi={recomputeKpi(kpis[key])} />
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/** Aggregates daily KPIs back into a single overall row when no server summary is provided. */
const buildOverallKpis = (dailyReports, overallSummary) => {
    if (overallSummary?.kpis) return overallSummary.kpis;
    if (!dailyReports?.length) return null;
    const kpiKeys = Object.keys(dailyReports[0]?.kpis || {});
    if (!kpiKeys.length) return null;
    const kpis = {};
    kpiKeys.forEach((key) => {
        let totalPass = 0;
        let total = 0;
        let title = '';
        let target = '';
        dailyReports.forEach((report) => {
            const recalc = recomputeKpi(report.kpis?.[key]);
            totalPass += recalc.totalPass || 0;
            total += recalc.total || 0;
            if (!title && recalc.title) {
                title = recalc.title;
                target = recalc.target;
            }
        });
        const score = total > 0 ? Math.round((totalPass / total) * 100) : 0;
        kpis[key] = { title, target, totalPass, total, score };
    });
    return kpis;
};

const TransferSlaSummary = ({ dailyReports, overallSummary, hideDailyBreakdown = false }) => {
    if (!dailyReports?.length) return <p>No transfer SLA data available.</p>;

    const overallKpis = buildOverallKpis(dailyReports, overallSummary);
    const isMultiDay = dailyReports.length > 1;
    const showOverall = overallKpis && (hideDailyBreakdown || isMultiDay);

    return (
        <div>
            {showOverall && <KpiTable title="Overall Summary" kpis={overallKpis} />}
            {!hideDailyBreakdown && dailyReports.map((report) => (
                <KpiTable key={report.date} title={report.date} kpis={report.kpis} />
            ))}
        </div>
    );
};

export default TransferSlaSummary;
